import { useState, useMemo, useEffect } from 'react';
import './UserManagement.css';
import { FEATURES, FEATURE_NAMES } from '../../utils/constants';
import UserTable from './components/UserTable';
import UserRoleUpdateSideModal from './components/UserRoleUpdateSideModal';
import UserDeleteModal from './components/UserDeleteModal';
import AppendRoleSideModal from './components/AppendRoleSideModal';
import AppendUserSideModal from './components/AppendUserSideModal';
import DeleteRoleModal from './components/DeleteRoleModal';
import SaveConfirmModal from './components/SaveConfirmModal';
import { useUserRoles, useUsers, useAddRoles, useRemoveRoles, useSetRole, useUnsetRole, useAddUser, useDeleteUser } from '../../hooks/queries/userRoleQueries';
import { useRoleFeatureClaims, useUpdateRoleFeatureClaims } from '../../hooks/queries/roleFeatureQueries';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import PlusIcon from '../../assets/icons/plus.svg?react';
import RefreshIcon from '../../assets/icons/refresh.svg?react';
import { isSystemAdmin } from '../../utils/permissions';
const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('users');
  
  // 사용자 관리 모달 상태
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [appendUserModalOpen, setAppendUserModalOpen] = useState(false);
  const [deleteRoleModalOpen, setDeleteRoleModalOpen] = useState(false);
  
  // 역할 관리 모달 상태
  const [appendRoleModalOpen, setAppendRoleModalOpen] = useState(false);
  const [saveConfirmModalOpen, setSaveConfirmModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  // API 훅들
  const addRolesMutation = useAddRoles();
  const updateRoleFeatureClaimsMutation = useUpdateRoleFeatureClaims();
  const setRoleMutation = useSetRole();
  const unsetRoleMutation = useUnsetRole();
  const addUserMutation = useAddUser();
  const deleteUserMutation = useDeleteUser();
  const removeRolesMutation = useRemoveRoles();
  // 원래 사용자 역할 저장 (모달 열 때 초기 상태 저장용)
  const [originalUserRoles, setOriginalUserRoles] = useState([]);
  
  // 초기 상태 저장 (역할 관리 탭용)
  const [initialRoleFeatureMap, setInitialRoleFeatureMap] = useState({});
  
  // API로 데이터 가져오기
  const { data: rolesData, isLoading: rolesLoading, error: rolesError } = useUserRoles();
  const { data: usersData, isLoading: usersLoading, error: usersError } = useUsers();
  const { data: roleFeatureClaimsData } = useRoleFeatureClaims();
  
  // 역할 목록 추출
  const roles = useMemo(() => {
    if (!rolesData?.Items) return [];
    return rolesData.Items.map(item => item.RoleName).filter(role => role !== "SystemAdmin");
  }, [rolesData]);
  
  // 사용자 목록 추출
  const users = useMemo(() => {
    if (!usersData?.Items) return [];
    return usersData.Items.map((item, index) => ({
      id: item.UserSysId || index + 1,
      email: item.Email || item.UserId,
      roles: item.Roles || []
    }));
  }, [usersData]);
  
  // 피처 목록
  const features = Object.entries(FEATURES).map(([key, value]) => ({
    id: value,
    name: key,
    displayName: FEATURE_NAMES[value] || value
  }));
  
  // 역할-피처 매핑 (API 데이터 기반)
  const roleFeatureMap = useMemo(() => {
    if (!roleFeatureClaimsData?.Items || !roles.length) {
      return {};
    }
    
    const map = {};
    roles.forEach(roleName => {
      const roleFeature = roleFeatureClaimsData.Items.find(
        item => item.RoleName === roleName
      );
      map[roleName] = roleFeature?.FeatureNames || [];
    });
    
    return map;
  }, [roleFeatureClaimsData, roles]);
  
  // 역할-피처 매핑 상태 (역할 관리 탭에서 수정용)
  const [localRoleFeatureMap, setLocalRoleFeatureMap] = useState({});
  
  // API 데이터가 로드되면 localRoleFeatureMap과 초기 상태 초기화
  useEffect(() => {
    if (Object.keys(roleFeatureMap).length > 0 && Object.keys(localRoleFeatureMap).length === 0) {
      // 깊은 복사로 초기 상태 저장
      const initialMap = JSON.parse(JSON.stringify(roleFeatureMap));
      setInitialRoleFeatureMap(initialMap);
      setLocalRoleFeatureMap(roleFeatureMap);
    }
  }, [roleFeatureMap, localRoleFeatureMap]);
  
  // 변경 감지: 현재 상태와 초기 상태 비교
  const hasChanges = useMemo(() => {
    if (Object.keys(initialRoleFeatureMap).length === 0) {
      return false;
    }
    
    // 역할 목록이 같은지 확인
    const initialRoles = Object.keys(initialRoleFeatureMap).sort();
    const currentRoles = Object.keys(localRoleFeatureMap).sort();
    
    if (initialRoles.length !== currentRoles.length) {
      return true;
    }
    
    for (let i = 0; i < initialRoles.length; i++) {
      if (initialRoles[i] !== currentRoles[i]) {
        return true;
      }
    }
    
    // 각 역할의 피처 배열 비교
    for (const role of initialRoles) {
      const initialFeatures = (initialRoleFeatureMap[role] || []).sort();
      const currentFeatures = (localRoleFeatureMap[role] || []).sort();
      
      if (initialFeatures.length !== currentFeatures.length) {
        return true;
      }
      
      for (let i = 0; i < initialFeatures.length; i++) {
        if (initialFeatures[i] !== currentFeatures[i]) {
          return true;
        }
      }
    }
    
    return false;
  }, [initialRoleFeatureMap, localRoleFeatureMap]);
  
  // 체크박스 토글 핸들러 (역할 관리 탭용)
  const toggleFeature = (role, featureId) => {
    setLocalRoleFeatureMap(prev => {
      const roleFeatures = prev[role] || [];
      const hasFeature = roleFeatures.includes(featureId);
      
      return {
        ...prev,
        [role]: hasFeature 
          ? roleFeatures.filter(f => f !== featureId)
          : [...roleFeatures, featureId]
      };
    });
  };

  // 사용자 수정 모달 열기
  const handleEditUser = (user) => {
    // 원래 역할 저장 (깊은 복사)
    setOriginalUserRoles([...user.roles]);
    setSelectedUser({ ...user });
    setEditModalOpen(true);
  };

  // 사용자 수정 모달 닫기
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedUser(null);
    setOriginalUserRoles([]);
  };

  // 사용자 삭제 모달 열기
  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  // 사용자 역할 수정 저장
  const handleSaveUserRoles = async () => {
    if (!selectedUser) return;

    try {
      const originalRoles = originalUserRoles || [];
      const currentRoles = selectedUser.roles || [];
      const userSysId = selectedUser.id; // UserSysId

      // 추가된 역할 찾기
      const addedRoles = currentRoles.filter(role => !originalRoles.includes(role));
      // 제거된 역할 찾기
      const removedRoles = originalRoles.filter(role => !currentRoles.includes(role));

      // 추가된 역할 부여
      const addPromises = addedRoles.map(role => {
        return setRoleMutation.mutateAsync({
          roleName: role,
          userSysIds: [userSysId]
        });
      });

      // 제거된 역할 제거
      const removePromises = removedRoles.map(role => {
        return unsetRoleMutation.mutateAsync({
          roleName: role,
          userSysIds: [userSysId]
        });
      });

      // 모든 API 호출 완료 대기
      await Promise.all([...addPromises, ...removePromises]);


      // 모달 닫기
      setEditModalOpen(false);
      setSelectedUser(null);
      setOriginalUserRoles([]);

    } catch (error) {
      console.error('❌ 사용자 역할 수정 실패:', error);
      alert('사용자 역할 수정에 실패했습니다.');
      throw error;
    }
  };

  // 사용자 추가 핸들러
  const handleAddUser = async ({ email, password }) => {
    try {
      await addUserMutation.mutateAsync({ email, password });
    } catch (error) {
      throw error;
      alert('사용자 추가에 실패했습니다.');
    }
  };

  // 사용자 삭제 확인
  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    try {
      const userSysId = selectedUser.id; // UserSysId
      await deleteUserMutation.mutateAsync(userSysId);
      setDeleteModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      alert('사용자 삭제에 실패했습니다.');
    }
  };

  // 역할 토글 (수정 모달 내)
  const toggleUserRole = (roleName) => {
    setSelectedUser(prev => {
      const hasRole = prev.roles.includes(roleName);
      return {
        ...prev,
        roles: hasRole
          ? prev.roles.filter(r => r !== roleName)
          : [...prev.roles, roleName]
      };
    });
  };

  // 역할 추가 핸들러
  const handleAddRole = async (roleName) => {
    try {
      await addRolesMutation.mutateAsync([roleName]);
    } catch (error) {
      alert('역할 추가에 실패했습니다.');
      throw error;
    }
  };

  // 초기화 핸들러
  const handleReset = () => {
    if (window.confirm('변경사항을 초기화하시겠습니까?')) {
      // 초기 상태로 복원
      const resetMap = JSON.parse(JSON.stringify(initialRoleFeatureMap));
      setLocalRoleFeatureMap(resetMap);
    }
  };

  // 저장 핸들러
  const handleSave = () => {
    setSaveConfirmModalOpen(true);
  };

  // 변경된 역할 찾기
  const getChangedRoles = useMemo(() => {
    if (Object.keys(initialRoleFeatureMap).length === 0) {
      return [];
    }
    
    const changedRoles = [];
    
    // 모든 역할 확인 (초기 상태와 현재 상태 모두)
    const allRoles = new Set([
      ...Object.keys(initialRoleFeatureMap),
      ...Object.keys(localRoleFeatureMap)
    ]);
    
    allRoles.forEach(role => {
      const initialFeatures = (initialRoleFeatureMap[role] || []).sort();
      const currentFeatures = (localRoleFeatureMap[role] || []).sort();
      
      // 배열 비교
      if (initialFeatures.length !== currentFeatures.length) {
        changedRoles.push(role);
      } else {
        for (let i = 0; i < initialFeatures.length; i++) {
          if (initialFeatures[i] !== currentFeatures[i]) {
            changedRoles.push(role);
            break;
          }
        }
      }
    });
    
    return changedRoles;
  }, [initialRoleFeatureMap, localRoleFeatureMap]);

  // 저장 확인 핸들러
  const handleConfirmSave = async () => {
    try {
      const changedRoles = getChangedRoles;
      
      if (changedRoles.length === 0) {
        return;
      }
      
      
      // 각 변경된 역할에 대해 API 호출
      const promises = changedRoles.map(role => {
        const featureNames = localRoleFeatureMap[role] || [];
        
        return updateRoleFeatureClaimsMutation.mutateAsync({
          roleName: role,
          featureNames: featureNames
        });
      });
      
      // 모든 API 호출 완료 대기
      await Promise.all(promises);
      
      
      // 저장 성공 후 초기 상태 업데이트
      const savedMap = JSON.parse(JSON.stringify(localRoleFeatureMap));
      setInitialRoleFeatureMap(savedMap);
      
    } catch (error) {
      alert('저장에 실패했습니다.');
      throw error; // SaveConfirmModal에서 처리하도록 에러를 다시 throw
    }
  };

  // 역할 삭제 핸들러
  const handleDeleteRole = async (roleName) => {
    try {
      let result = await removeRolesMutation.mutateAsync([roleName]);
      if (Object.keys(result.FailRoles).length > 0) {
        throw new Error( `해당 역할을 가진 사용자가 있어 삭제할 수 없습니다.`);
      }
    } catch (error) {
      alert(error.message || '역할 삭제에 실패했습니다.');
    }
  };

  return (
    <div className="user-management-container page-container">

      {/* 탭 네비게이션 */}
      <div className="tabs grid-cols-2">
        <button 
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          사용자 관리
        </button>
        <button 
          className={`tab ${activeTab === 'roles' ? 'active' : ''}`}
          onClick={() => setActiveTab('roles')}
        >
          역할 관리
        </button>
      </div>

      {/* 탭 콘텐츠 */}
      <div className="tab-content">
        {/* 사용자 관리 탭 */}
        {activeTab === 'users' && (
          <div className="users-tab">
            <div className="flex justify-end align-center my-10 gap-10">
              <div className="flex gap-10">
                {isSystemAdmin() && (
                <button 
                  className="btn btn-primary big-btn flex align-center gap-3"
                  onClick={() => setAppendUserModalOpen(true)}
                >
                  <PlusIcon color='var(--white)'/>
                  <p className="text-b2-400 text-white">사용자 추가</p>
                </button>
                )}
              </div>
            </div>

            <UserTable
              users={users}
              isLoading={usersLoading}
              isError={usersError}
              roleFeatureMap={roleFeatureMap}
              onEditUser={handleEditUser}
              onDeleteUser={handleDeleteUser}
            />
          </div>
        )}

        {/* 역할 관리 탭 */}
        {activeTab === 'roles' && (
          <div className="roles-tab">
            <div className="flex justify-end align-center my-10">

              <div className="flex gap-10">
                <button 
                  className="btn btn-style-5 big-btn flex align-center gap-3"
                  onClick={() => setAppendRoleModalOpen(true)}
                >
                  <PlusIcon color='var(--gray-600)'/>
                  <p className="text-b2-400 text-gray-600">역할 추가</p>
                </button>
                <button 
                  className="btn btn-style-5 big-btn flex align-center gap-3"
                  onClick={handleReset}
                  disabled={!hasChanges}
                >
                  <RefreshIcon color='var(--gray-600)'/>
                  <p className="text-b2-400 text-gray-600">초기화</p>
                </button>
                <button 
                  className="btn btn-primary big-btn"
                  onClick={handleSave}
                  disabled={!hasChanges}
                >
                  <span>저장</span>
                </button>
              </div>
            </div>

            <div className="primary-table role-feature-table">
              <table>
                <thead>
                  <tr>
                    <th width="200px">피처</th>
                    {roles.map(role => (
                      <th key={role} className="text-center">
                        <div className="flex align-center justify-center gap-2">
                          <p className="text-b1-600 text-gray-800 text-center">
                            {role}
                          </p>
                          {
                            role !== "User" && (
                              <DeleteIcon className="cursor-pointer" color="var(--gray-600)" onClick={()=>{setSelectedRole(role); setDeleteRoleModalOpen(true);}} />
                            )
                          }
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map(feature => (
                    <tr key={feature.id}>
                      <td className="feature-name-cell">
                        <div className="feature-info">
                          <span className="feature-display-name">{feature.displayName}</span>
                        </div>
                      </td>
                      {roles.map(role => (
                        <td key={`${role}-${feature.id}`} className="checkbox-cell">
                          <label className="checkbox-wrapper">
                            <input
                              type="checkbox"
                              checked={localRoleFeatureMap[role]?.includes(feature.id) || false}
                              onChange={() => toggleFeature(role, feature.id)}
                            />
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          </div>
        )}
      </div>

      {/* 사용자 수정 모달 (Side Modal) */}
      {editModalOpen && (
        <UserRoleUpdateSideModal
          user={selectedUser}
          roles={roles}
          roleFeatureMap={roleFeatureMap}
          onClose={handleCloseEditModal}
          onSave={handleSaveUserRoles}
          onToggleRole={toggleUserRole}
        />
      )}

      {/* 사용자 삭제 확인 모달 */}
      {deleteModalOpen && (
        <UserDeleteModal
          user={selectedUser}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleConfirmDelete}
        />
      )}

      {/* 역할 추가 모달 */}
      {appendRoleModalOpen && (
        <AppendRoleSideModal
          onClose={() => setAppendRoleModalOpen(false)}
          onSave={handleAddRole}
        />
      )}

      {/* 사용자 추가 모달 */}
      {appendUserModalOpen && (
        <AppendUserSideModal
          onClose={() => setAppendUserModalOpen(false)}
          onSave={handleAddUser}
        />
      )}

      {/* 저장 확인 모달 */}
      {saveConfirmModalOpen && (
        <SaveConfirmModal
          onClose={() => setSaveConfirmModalOpen(false)}
          onConfirm={handleConfirmSave}
        />
      )}

      {/* 역할 삭제 모달 */}
      {deleteRoleModalOpen && (
        <DeleteRoleModal
          role={selectedRole}
          onClose={() => setDeleteRoleModalOpen(false)}
          onConfirm={handleDeleteRole}
        />
      )}
    </div>
  );
};

export default UserManagement; 